import { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('student_token'));
  const [loading, setLoading] = useState(true);

  // Verify token on mount
  useEffect(() => {
    const verifyAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/student/verify-token`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (res.ok) {
          const data = await res.json();
          // Fetch full profile
          const profileRes = await fetch(`${API_URL}/api/student/profile`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (profileRes.ok) {
            const profile = await profileRes.json();
            setUser(profile);
          }
        } else {
          // Token invalid, clear it
          localStorage.removeItem('student_token');
          setToken(null);
        }
      } catch (error) {
        console.error('Auth verification failed:', error);
        localStorage.removeItem('student_token');
        setToken(null);
      } finally {
        setLoading(false);
      }
    };

    verifyAuth();
  }, [token]);

  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/api/student/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await res.json();
    localStorage.setItem('student_token', data.access_token);
    setToken(data.access_token);

    // Fetch profile
    const profileRes = await fetch(`${API_URL}/api/student/profile`, {
      headers: { 'Authorization': `Bearer ${data.access_token}` }
    });
    if (profileRes.ok) {
      const profile = await profileRes.json();
      setUser(profile);
    }

    return data;
  };

  const register = async (firstName, lastName, email, password) => {
    const res = await fetch(`${API_URL}/api/student/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        first_name: firstName,
        last_name: lastName,
        email,
        password
      })
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Registration failed');
    }

    const data = await res.json();
    localStorage.setItem('student_token', data.access_token);
    setToken(data.access_token);

    // Set user from registration data
    setUser({
      email,
      first_name: firstName,
      last_name: lastName,
      plan: 'free'
    });

    return data;
  };

  const logout = () => {
    localStorage.removeItem('student_token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
